import React, { useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { PrinterOutlined } from "@ant-design/icons";
import { App, Alert, Badge, Button, Card, Collapse, Grid, Typography } from "antd";
import SalesTaxLedgerFilters from "./components/SalesTaxLedgerRecordFilters";
import SalesTaxLedgerTable   from "./components/SalesTaxLedgerRecordTable";

import ReportDom  from "../../../components/common/ReportDom";
import { useReactToPrint } from "react-to-print";
import { fetchVatFilingStatus } from "./vatFilingSlice";
import VatFilingStatusPanel from "./VatFilingStatusPanel";


const { Title } = Typography;
const { Panel } = Collapse;

const SalesTaxLedger = () => {
  const dispatch   = useDispatch();                                 
  const { filters }  = useSelector((s) => s.salesTaxLedger);
  const { quarters } = useSelector((s) => s.vatFiling);
  const authInfo     = useSelector((s) => s.auth?.auth_info);
  const { message }  = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const isUAE    = authInfo?.country?.toLowerCase() !== "india";
  const overdueQ = quarters.filter((q) => q.status === "overdue");
  const dueQ     = quarters.filter((q) => q.status === "due");
  const alertCount = overdueQ.length + dueQ.length;

  const reportRef   = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Sales Tax Ledger",
    removeAfterPrint: true,
    pageStyle: "@page { size: A4 landscape; margin: 12mm; }",
  });

  useEffect(() => {                                                 
    if (isUAE) dispatch(fetchVatFilingStatus());
  }, [dispatch, isUAE]);

  return (
    <div className="p-4">
      <App>

        {/* Header */}
        <Card
          className="shadow-md rounded-xl mb-4"
          title={
            <Title level={4} style={{ margin: 0 }} className="reportBigTitle">
              Duty & Tax Account Ledger
            </Title>
          }
          extra={
            <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
              {!screens.xs && "Print"}
            </Button>
          }
          styles={{
            header: { borderBottom: "none", padding: "12px 16px" },
            body:   { padding: 0 },
          }}
        />

        {/* Overdue alert */}
        {isUAE && overdueQ.length > 0 && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              <strong>
                {overdueQ.length} VAT quarter
                {overdueQ.length > 1 ? "s are" : " is"} overdue!
                File immediately to avoid FTA penalties.
              </strong>
            }
            description={`Overdue: ${overdueQ.map((q) => q.label).join(", ")}`}
          />
        )}

        {/* Due alert */}
        {isUAE && dueQ.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              <strong>
                VAT filing deadline approaching for{" "}
                {dueQ.map((q) => q.label).join(", ")}.
              </strong>
            }
          />
        )}

        {/* Filters */}
        <SalesTaxLedgerFilters messageApi={message} />

        {/* VAT Filing Status panel */}
        {isUAE && (
          <Collapse
            className="mb-4 shadow-md rounded-xl"
            defaultActiveKey={alertCount > 0 ? ["vat-status"] : []}
          >
            <Panel
              key="vat-status"
              header={
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  VAT Filing Status
                  {alertCount > 0 && (
                    <Badge
                      count={alertCount}
                      style={{
                        backgroundColor:
                          overdueQ.length > 0 ? "#ff4d4f" : "#faad14",
                      }}
                    />
                  )}
                </span>
              }
            >
              <VatFilingStatusPanel />                               {/* ← uncommented */}
            </Panel>
          </Collapse>
        )}

        {/* Tax ledger table */}
        <Card className="shadow-md rounded-xl">
          <SalesTaxLedgerTable />
        </Card>

      </App>

      {/* Hidden print layout */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Duty & Tax Account Ledger"
          mode="report-page"
          filters={filters}
          renderTable={() => <SalesTaxLedgerTable printMode={true} />}
        />
      </div>
    </div>
  );
};

export default SalesTaxLedger;